import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { clsx } from 'clsx'

import { domainVerificationsQuery, domainVerificationsQueryKey } from './queries'
import VerificationModal from './VerificationModal'

import styles from './DomainVerificationSection.module.scss'

import { checkDomainVerification, initiateDomainVerification, updateDomainCapturePolicy } from '@/api/enterprise'
import { DomainVerificationRecord, DomainVerificationStatus } from '@/api/generated/api'
import Button from '@/components/Button'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import { useToast } from '@/components/Toast/ToastContext'
import { useAnalytics } from '@/hooks/useAnalytics'
import { useHasDepartments } from '@/hooks/useHasDepartments'
import PermissionTooltip from '@/pages/app/Settings/PermissionTooltip'
import { selectIsAdmin } from '@/store/features/user/userSlice'
import { DomainCapturePolicy } from '@/types'
import { compactAmPm } from '@/utils/compactAmPm'
import { EventName } from '@/utils/eventDefinitions'

type DomainVerificationTextSize = 'default' | 'comfortable'

interface DomainVerificationSectionProps {
  textSize?: DomainVerificationTextSize
}

const getDomainCaptureOptions = (hasDepts: boolean): Array<{ id: DomainCapturePolicy; title: string; helperText: string }> => [
  { id: DomainCapturePolicy.Off, title: 'Off', helperText: 'Do not restrict new sign-ups on this domain' },
  {
    id: DomainCapturePolicy.Require,
    title: hasDepts ? 'Require organization membership' : 'Require team membership',
    helperText: 'Users signing up with this domain must join this enterprise to use Flow',
  },
]

const getDomainCaptureDropdownItems = (currentValue: DomainCapturePolicy, hasDepts: boolean): DropdownItem[] =>
  getDomainCaptureOptions(hasDepts).map((option) => ({ ...option, selected: option.id === currentValue }))

const formatSubStatus = (domain: DomainVerificationStatus): string => {
  if (domain.verified && domain.verified_at) return `Verified ${new Date(domain.verified_at).toLocaleDateString()}`
  if (domain.verification_attempts > 0 && domain.last_verification_check_at) {
    return `Last checked ${compactAmPm(new Date(domain.last_verification_check_at).toLocaleString())}`
  }
  return 'Pending verification'
}

const DomainRow = ({ domain }: { domain: DomainVerificationStatus }) => {
  const queryClient = useQueryClient()
  const { updateToast } = useToast()
  const { trackEvent } = useAnalytics()
  const isAdmin = useSelector(selectIsAdmin)
  const hasDepts = useHasDepartments()

  const [optimisticPolicy, setOptimisticPolicy] = useState<DomainCapturePolicy | null>(null)
  const [record, setRecord] = useState<DomainVerificationRecord | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const effectivePolicy = optimisticPolicy ?? domain.domain_capture_policy
  const items = useMemo(() => getDomainCaptureDropdownItems(effectivePolicy, hasDepts), [effectivePolicy, hasDepts])

  // Dropdown calls setItems before onSelect on each click; route those flips
  // through our single optimistic-override instead of holding a parallel copy.
  const setItemsForDropdown = useCallback<React.Dispatch<React.SetStateAction<DropdownItem[]>>>(
    (action) => {
      const next = typeof action === 'function' ? action(items) : action
      const selected = next.find((item) => item.selected)
      if (selected && selected.id !== effectivePolicy) {
        setOptimisticPolicy(selected.id as DomainCapturePolicy)
      }
    },
    [items, effectivePolicy]
  )

  useEffect(() => {
    if (domain.verified) {
      setRecord(null)
      setIsModalOpen(false)
    }
  }, [domain.verified])

  const updatePolicyMutation = useMutation({
    mutationFn: async (policy: DomainCapturePolicy) => updateDomainCapturePolicy(domain.domain_id, policy),
    onSuccess: (_response, policy) => {
      trackEvent?.(EventName.SettingChanged, {
        setting: `domainCapture:${domain.domain}`,
        value: policy,
        success: true,
      })
      updateToast('Domain capture policy updated', 'success')
      void queryClient.invalidateQueries({ queryKey: domainVerificationsQueryKey })
    },
    onError: (err: unknown) => {
      setOptimisticPolicy(null)
      updateToast((err as Error)?.message || 'Failed to update domain capture policy', 'error')
    },
  })

  const initiateMutation = useMutation({
    mutationFn: async () => initiateDomainVerification(domain.domain_id),
    onSuccess: (response) => {
      setRecord(response.data.record ?? null)
      updateToast('Verification token generated', 'success')
      void queryClient.invalidateQueries({ queryKey: domainVerificationsQueryKey })
    },
    onError: (err: unknown) => updateToast((err as Error)?.message || 'Failed to initiate verification', 'error'),
  })

  const checkMutation = useMutation({
    mutationFn: async () => checkDomainVerification(domain.domain_id),
    onSuccess: (response) => {
      const verifiedNow = response.data.verified
      updateToast(
        verifiedNow ? 'Domain verified successfully' : 'TXT record not found yet. Try again in a minute.',
        verifiedNow ? 'success' : 'error'
      )
      if (verifiedNow) {
        setRecord(null)
        setIsModalOpen(false)
      }
      void queryClient.invalidateQueries({ queryKey: domainVerificationsQueryKey })
    },
    onError: (err: unknown) => updateToast((err as Error)?.message || 'Failed to check verification', 'error'),
  })

  const openVerificationModal = () => {
    setIsModalOpen(true)
    if (!record && !initiateMutation.isPending) initiateMutation.mutate()
  }

  const handleSelect = (selectedItems: DropdownItem[]) => {
    if (selectedItems.length === 0) return
    const value = selectedItems[0].id as DomainCapturePolicy
    if (value === domain.domain_capture_policy) return
    setOptimisticPolicy(value)
    updatePolicyMutation.mutate(value)
  }

  const policyDisabled = !isAdmin || updatePolicyMutation.isPending

  return (
    <>
      <div className={styles.row}>
        <div className={styles.rowLeft}>
          <div className={styles.domain}>{domain.domain}</div>
          <div className={styles.statusLine}>
            <span className={domain.verified ? styles.verifiedPill : styles.pendingPill}>
              {domain.verified ? 'Verified' : 'Unverified'}
            </span>
            {domain.verified && <span>{formatSubStatus(domain)}</span>}
          </div>
        </div>
        <div className={styles.rowRight}>
          {domain.verified ? (
            <PermissionTooltip>
              <Dropdown
                items={items}
                setItems={setItemsForDropdown}
                onSelect={handleSelect}
                placeholder={domain.domain_capture_policy === DomainCapturePolicy.Unset ? 'Not set' : 'Select policy'}
                className={styles.policyDropdown}
                disabled={policyDisabled}
              />
            </PermissionTooltip>
          ) : (
            <Button
              title={record ? 'Continue verification' : 'Start verification'}
              type="dark"
              onClick={openVerificationModal}
              isDisabled={!isAdmin}
              className={styles.smallButton}
            />
          )}
        </div>
      </div>
      <VerificationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        domain={domain}
        record={record}
        isInitiatePending={initiateMutation.isPending}
        onCheck={() => checkMutation.mutate()}
        isCheckPending={checkMutation.isPending}
        isAdmin={isAdmin}
      />
    </>
  )
}

const DomainVerificationSection = ({ textSize = 'default' }: DomainVerificationSectionProps) => {
  const isAdmin = useSelector(selectIsAdmin)
  const isComfortable = textSize === 'comfortable'
  const comfortable = isComfortable && styles.comfortableCopy
  const { data: response, isLoading, error } = useQuery({ ...domainVerificationsQuery(), enabled: isAdmin })
  const domains = useMemo(() => response?.data.domains ?? [], [response])

  if (!isAdmin)
    return <div className={clsx(styles.empty, comfortable)}>Only admins can see domain verification status.</div>
  if (isLoading) return <div className={clsx(styles.loading, comfortable)}>Loading domains…</div>
  if (error) return <div className={clsx(styles.errorBox, comfortable)}>Failed to load domains.</div>
  if (domains.length === 0)
    return (
      <div className={clsx(styles.empty, comfortable)}>
        Your organization is not linked to any domains. Contact{' '}
        <a href="mailto:enterprise@wispr.ai">enterprise@wispr.ai</a> to add one.
      </div>
    )

  return (
    <div className={clsx(styles.section, isComfortable && styles.sectionComfortable)}>
      <div className={styles.list}>
        {domains.map((d) => (
          <DomainRow key={d.domain_id} domain={d} />
        ))}
      </div>
    </div>
  )
}

export default DomainVerificationSection
